import { FormControl, Select as MuiSelect, withStyles, MenuItem } from '@material-ui/core';
import React from 'react';
import PropTypes from 'prop-types';
import colors from 'utils/stylesheet/colors';
import './styles.scss';
import { ReactComponent as DownArrow } from 'assets/images/down.svg';

const CustomFormControl = withStyles({
  root: {
    borderRadius: '4px',
    boxSizing: `border-box`,
    height: (props) => (props.radius ? '30px' : '50px'),
    margin: '0.25rem 1rem 1rem 0',
    '&.Mui-selected': {
      outline: 'none'
    }
  }
})((props) => <FormControl {...props} style={{ width: props.width }} />);

export const CustomSelect = withStyles({
  root: {
    borderRadius: (props) => props.radius || '4px',
    color: colors.white,
    fontWeight: 'bold',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    lineHeight: '21px',
    padding: (props) => (props.radius ? '0.475rem 1rem' : '0.875rem 1rem'),
    background: colors.highlight_dark_blue,
    borderBottom: (props) => (props.error ? '1px solid red' : 'none'),
    height: '22px',
    '&:focus': {
      borderRadius: (props) => props.radius || '4px'
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed'
    }
  },
  icon: {
    top: (props) => (props.radius ? '1rem' : '1.35rem'),
    right: '0.75rem'
  }
})((props) => (
  <MuiSelect
    displayEmpty
    MenuProps={{
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      getContentAnchorEl: null,
      PaperProps: {
        square: false,
        style: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          background: colors.white,
          border: `2px solid ${colors.border_blue}`,
          maxHeight: '15rem'
        }
      }
    }}
    IconComponent={DownArrow}
    disableUnderline
    {...props}
  />
));

const CustomMenuItem = withStyles({
  root: {
    height: '2.25rem',
    color: colors.blackish,
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '17px'
  }
})(React.forwardRef((props, ref) => <MenuItem {...props} ref={ref} />));

const getRenderValue = (props, selected) => {
  if (selected.length === 0) {
    return <div className='multi-placeholder'>{props.placeholder}</div>;
  }
  return props.options
    .filter((option) => selected.includes(typeof option === 'string' ? option : option.value))
    .map((option) => (typeof option === 'string' ? option : option.name))
    .join(', ');
};

const Select = (props) => {
  const {
    multi,
    width,
    borderRadius,
    label,
    disabled,
    placeholder,
    selectedValue,
    options,
    onSelectionChange,
    error
  } = props;
  const extraProps = {};
  if (multi) {
    extraProps.renderValue = (selected) => getRenderValue(props, selected);
  }
  return (
    <CustomFormControl width={width} radius={borderRadius}>
      {label && <div className='select-label'>{label}</div>}
      <CustomSelect
        multiple={multi}
        disabled={disabled}
        className={`custom-select${placeholder && selectedValue === '' ? ' placeholder' : ''}`}
        value={selectedValue}
        {...extraProps}
        onChange={({ target: { value } }) => onSelectionChange(value)}
        radius={borderRadius}
        error={error}
      >
        {placeholder && (
          <CustomMenuItem value='' disabled>
            {placeholder}
          </CustomMenuItem>
        )}
        {options.map((option, i) => (
          <CustomMenuItem value={option.value ?? option} key={option.id || i}>
            {option.name ?? option}
          </CustomMenuItem>
        ))}
      </CustomSelect>
    </CustomFormControl>
  );
};

Select.propTypes = {
  selectedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object
  ]).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  label: PropTypes.string,
  multi: PropTypes.bool,
  borderRadius: PropTypes.string,
  error: PropTypes.bool
};

Select.defaultProps = {
  disabled: false,
  width: '100%',
  multi: false,
  placeholder: '',
  label: '',
  borderRadius: '',
  error: false
};

export default Select;
